/**
 * Phase 329 wave 4 task 4.2 — the ONE seam between the form wizard and the engine surface
 * driving it.
 *
 * <p>This surface now serves two audiences over the SAME declarative form grammar: a client
 * following a single-use magic link (`formApi.ts` — the credential is in the request body) and a
 * signed-in member of staff following a credential-free deep link (`staffFormApi.ts` — an Entra
 * bearer in the header). What they render is identical, because it is the same declaration
 * rendered by the same components; what differs is four HTTP calls and the identity in front of
 * them.</p>
 *
 * <p>So the difference is expressed as an OBJECT the page is handed, not as a branch inside it.
 * `FormExperience` — steps, progress, validation, auto-save, resume, submit, done-state — has no
 * idea which surface it is on, cannot be given one by accident, and gains a third audience by
 * gaining a third implementation of this interface rather than a third `if`.</p>
 *
 * <h3>Neither method rejects</h3>
 * <p>Both {@link FormBackend.load} and {@link FormBackend.submit} resolve to a typed outcome
 * instead of throwing, because the outcomes that matter are not all failures: "the session went
 * away and a sign-in redirect is already under way" is a THIRD thing, and modelling it as an
 * exception is how it ends up rendered as "something went wrong" over a page that is navigating
 * away. (`FormExperience` still guards both with a `catch` — a contract stated in a type is not
 * the same as one enforced at runtime.)</p>
 */
import type { FormValues, OpenHoldItem, Theme, ValidateResponse } from "../types.js";
import type { StatusVariant } from "../components/StatusCard.js";
import { FOLLOW_ON_MIN_RETRY_MS, type FollowOnResult, type LookupResult } from "./formApi.js";

/** What one attempt to open the form produced. */
export type FormLoadOutcome =
  /** The form, its branding, its ctx snapshot and any draft to resume from. */
  | { readonly kind: "ready"; readonly response: ValidateResponse }
  /** There is nothing to render and nothing the person can do about it here. */
  /**
   * BB walk 2 (item 5) — `theme` is the tenant branding the terminal page renders under, when the
   * surface knows it (a consumed or expired magic link still names its practice). Absent ⇒ the
   * platform neutral.
   */
  | { readonly kind: "unavailable"; readonly variant: StatusVariant; readonly theme?: Theme }
  /** A sign-in redirect has been started; hold the loading state, the page is leaving. */
  | { readonly kind: "signing-in" };

/**
 * Phase 572 wave 3 task 3.1 — the approval follow-on, carried by a staff submit that declared one
 * when the gate declares a `required_role` the signed-in operator holds on the job's own tenant.
 *
 * The link is NOT a credential: it is `{job_id, form_id}`, and authorisation is at sign-in, tenant
 * scoping at `OperatorTenantGuard`. It is the same posture `StaffFormsApi.cs:58-62` documents.
 */
export interface ApprovalFollowOn {
  /** The approval form's declared id. */
  readonly form_id: string;
  /** The credential-free deep link to the approval form for this job. */
  readonly link: string;
}

/** What one submit attempt produced. */
export type FormSubmitOutcome =
  | { readonly kind: "submitted" }
  /**
   * Phase 572 wave 3 task 3.1 — the staff-only arm: the submit succeeded and the operator has an
   * approval form to move to for the same job. The page navigates to `follow_on.link`, never to the
   * submitted screen.
   */
  | { readonly kind: "follow-on"; readonly follow_on: ApprovalFollowOn }
  /**
   * Recoverable: the answers are still on the page and the person can press submit again.
   *
   * `message` is the ENGINE'S OWN SENTENCE where the door refused with one (Phase 532 — an
   * approval form is refused in plain English by the shared gate validator, and a generic
   * "try again" over that would be a lie). Absent ⇒ the renderer's generic copy. It is display
   * copy and nothing else — nothing branches on it, and no code travels beside it.
   */
  | { readonly kind: "failed"; readonly message?: string }
  /** As above — a redirect is under way, so the person must not be shown a failure. */
  | { readonly kind: "signing-in" };

export interface FormBackend {
  /** Opens the form. Called once per backend identity (per token, or per {job, form}). */
  load(): Promise<FormLoadOutcome>;
  /**
   * Writes the part-completed answers. Background work nobody asked for: its result is never
   * surfaced, and it must not reject (both implementations swallow their own failures).
   */
  saveDraft(fields: FormValues): Promise<unknown>;
  /** Submits the completed answers. */
  submit(fields: FormValues): Promise<FormSubmitOutcome>;
  /**
   * Runs one schema-declared lookup, when the surface HAS a lookup endpoint.
   *
   * <p>Optional rather than mandatory: a surface without one renders no lookup control at all,
   * which is the honest outcome — a button that cannot reach an endpoint is worse than an
   * absent button on a form the person must always be able to finish by hand.</p>
   */
  readonly lookup?: (lookupId: string, args: Record<string, string>) => Promise<LookupResult>;
  /**
   * Settles one blocker on an approval gate, when the surface HAS a settle endpoint (staff
   * forms only).
   *
   * <p>Optional rather than mandatory: the client form surface does not have blockers, so it
   * does not need this method. When absent, the blocker panel is simply not rendered.</p>
   */
  readonly settleBlocker?: (
    item: OpenHoldItem,
    decision: "received" | "not_required",
    fields: { note?: string; received_date?: string; reason?: string },
  ) => Promise<{ kind: "settled" } | { kind: "failed"; message: string }>;
  /**
   * Phase 569 — asks ONCE for the declared follow-on link, for the form that has just been
   * submitted. Never rejects (every failure is `unavailable`).
   *
   * <p>Optional for the same reason as {@link FormBackend.lookup}: only the client (magic-link)
   * surface has a follow-on endpoint — its credential is the just-consumed token — and the engine
   * answers a staff-audience form with `unavailable` anyway. A surface without it shows the
   * declared fallback copy, never a dead link. The page reaches the network ONLY through this
   * seam, and polls it with {@link pollFollowOn} rather than a loop of its own.</p>
   */
  readonly fetchFollowOn?: () => Promise<FollowOnResult>;
}

/** Where a follow-on poll ended: the link, or nothing to show but the declared fallback. */
export type FollowOnSettled = Exclude<FollowOnResult, { readonly status: "pending" }>;

const SETTLED_UNAVAILABLE: FollowOnSettled = { status: "unavailable" };

/**
 * Polls {@link FormBackend.fetchFollowOn} until it settles — the ONE follow-on poll loop, so the
 * page holds no timing rules of its own.
 *
 * <ul>
 *   <li><b>`ready` / `unavailable` stop it at once.</b> `unavailable` is final by the endpoint's
 *   contract (no declaration, window passed, value refused), so it is never retried.</li>
 *   <li><b>`pending` waits at least `retry_after_ms`</b> — the server's value is a FLOOR, never
 *   shortened, and never below {@link FOLLOW_ON_MIN_RETRY_MS} whatever arrives.</li>
 *   <li><b>`budgetMs` bounds it</b> — the declared `poll_for`. A call that could only land past
 *   the budget is not made; the poll settles `unavailable` (the declared fallback) instead.</li>
 *   <li><b>`signal` cancels it</b> (the page unmounting): no further call is made, the pending
 *   wait is cut short, and it resolves `null` — there is no one left to show a result to.</li>
 * </ul>
 *
 * It never rejects.
 */
export async function pollFollowOn(
  fetchOnce: () => Promise<FollowOnResult>,
  budgetMs: number,
  signal?: AbortSignal,
): Promise<FollowOnSettled | null> {
  const deadline = Date.now() + budgetMs;
  for (;;) {
    if (signal?.aborted) return null;
    let outcome: FollowOnResult;
    try {
      outcome = await fetchOnce();
    } catch {
      // The seam's contract is "never rejects"; a type is not a runtime guarantee.
      return signal?.aborted ? null : SETTLED_UNAVAILABLE;
    }
    if (signal?.aborted) return null;
    if (outcome.status !== "pending") return outcome;

    const wait = Math.max(outcome.retry_after_ms, FOLLOW_ON_MIN_RETRY_MS);
    if (Date.now() + wait > deadline) return SETTLED_UNAVAILABLE;
    await abortableDelay(wait, signal);
  }
}

/** Resolves after `ms`, or as soon as `signal` aborts — never rejects. */
function abortableDelay(ms: number, signal?: AbortSignal): Promise<void> {
  return new Promise((resolve) => {
    const onAbort = () => {
      clearTimeout(timer);
      resolve();
    };
    const timer = setTimeout(() => {
      signal?.removeEventListener("abort", onAbort);
      resolve();
    }, ms);
    signal?.addEventListener("abort", onAbort, { once: true });
  });
}

/**
 * The PLATFORM CAP on a declared `poll_for`, in ms — the client half of the grammar's
 * `FORM_FOLLOW_ON_POLL_FOR_CAP` (PT2M; pinned to it by `FormExperience.followon.test.tsx`, read
 * from the grammar source). ARD-DSL-065 already refuses a larger budget at deploy time; the cap is
 * re-applied here so no value can keep a thank-you screen polling past the endpoint's own window.
 */
export const FOLLOW_ON_POLL_FOR_CAP_MS = 120_000;

/**
 * The xs:duration spelling the engine's `XmlConvert.ToTimeSpan` accepts — the same pattern the
 * grammar's `xsdDurationSeconds` reads: `PnYnMnDTnHnMnS` and its sub-forms.
 */
const XSD_DURATION =
  /^P(?=\d|T\d)(?:(\d+)Y)?(?:(\d+)M)?(?:(\d+)D)?(?:T(?=\d)(?:(\d+)H)?(?:(\d+)M)?(?:(\d+(?:\.\d+)?)S)?)?$/;

/**
 * The poll budget, in ms, for a declared `submitted.follow_on.poll_for` — what the page hands
 * {@link pollFollowOn}. Clamped to `[0, FOLLOW_ON_POLL_FOR_CAP_MS]`. A value that is not an
 * xs:duration (unreachable past ARD-DSL-065) is a ZERO budget: the page asks exactly once and,
 * on anything but `ready`, shows the declared fallback — never an unbounded poll.
 */
export function followOnBudgetMs(pollFor: string): number {
  const m = XSD_DURATION.exec(pollFor);
  if (m === null) return 0;
  const n = (i: number): number => (m[i] === undefined ? 0 : Number(m[i]));
  const seconds = ((n(1) * 365 + n(2) * 30 + n(3)) * 24 + n(4)) * 3600 + n(5) * 60 + n(6);
  return Math.min(Math.max(Math.round(seconds * 1000), 0), FOLLOW_ON_POLL_FOR_CAP_MS);
}
